import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import Card from '@/components/ui/Card'
import IssueCardModal from '@/components/IssueCardModal'
import TeacherOverviewPanel from '@/components/teacher/Overview'
import TeacherKycCard from '@/pages/teacher/components/TeacherKycCard'
import ProfileShare from './ProfileShare'

const KYC_ENABLED = import.meta.env.VITE_FEATURE_LITHIC_KYC === 'true'

interface ModalState {
  open: boolean
  merchant: string | null
}

export default function TeacherOverview() {
  const [modal, setModal] = useState<ModalState>({ open: false, merchant: null })

  const walmartUrl = import.meta.env.VITE_WALMART_BUSINESS_URL || 'https://business.walmart.com/'
  const amazonUrl = import.meta.env.VITE_AMAZON_BUSINESS_URL || 'https://business.amazon.com/'

  const openModal = (merchant: string) => setModal({ open: true, merchant })
  const closeModal = () => setModal({ open: false, merchant: null })

  return (
    <div className="space-y-8">
      {KYC_ENABLED && <TeacherKycCard />}

      <div>
        <Link
          to="/portal/teacher/messages"
          className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-500"
        >
          <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
          Messages
        </Link>
      </div>

      <TeacherOverviewPanel />

      <ProfileShare />

      <div className="rounded-2xl border border-sand bg-shell/40 p-4 text-sm text-cocoa/70">
        Prefer a quick one-off virtual card? Use the legacy card issuer below while the new shopping workflow rolls out.
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="space-y-4 p-5">
          <div>
            <h2 className="text-xl font-semibold text-cocoa">Walmart Business</h2>
            <p className="text-sm text-cocoa/70">
              Build your cart, then request a single-use, merchant-locked card equal to your cart total.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              href={walmartUrl}
              target="_blank"
              rel="noreferrer"
              className="btn bg-white text-cocoa border border-sand hover:bg-shell"
            >
              Open Walmart Business
            </a>
            <button type="button" className="btn" onClick={() => openModal('walmart.com')}>
              Issue One-Time Card
            </button>
          </div>
        </Card>

        <Card className="space-y-4 p-5">
          <div>
            <h2 className="text-xl font-semibold text-cocoa">Amazon Business</h2>
            <p className="text-sm text-cocoa/70">
              Shop for approved supplies and request a merchant-locked card for your exact cart amount.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              href={amazonUrl}
              target="_blank"
              rel="noreferrer"
              className="btn bg-white text-cocoa border border-sand hover:bg-shell"
            >
              Open Amazon Business
            </a>
            <button type="button" className="btn" onClick={() => openModal('amazon.com')}>
              Issue One-Time Card
            </button>
          </div>
        </Card>
      </div>

      <IssueCardModal open={modal.open} merchant={modal.merchant ?? undefined} onClose={closeModal} />
    </div>
  )
}
